/**
 * «Юниверситет» — вкладка кабинета оператора (28.09, ТЗ владельца): учебная
 * мини-RPG по филиалам. Подвкладки = все активные филиалы из конфигуратора EO,
 * подпись — имя филиала («Псих»). Какой филиал что показывает — настройка
 * филиала branches.uni_pack (конфигуратор EO), никаких сравнений с id/именем.
 * 29.09: последний чип ряда — «Grades» (свои грейды по квестам, uni/MyGrades.tsx).
 * 16.10: долгое нажатие по любому месту вкладки — замечание (uni/feedback); чип «Замечания» — их список.
 *
 * Филиалы и квесты — напрямую /api/branches и /api/quests: «useAppData у
 * оператора приходит пустым» (/api/master-classes требует права mc, и весь
 * Promise.all падает — см. ProfileTab).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { useAuth } from "../../auth";
import type { Branch, Quest } from "../../api/types";
import { BranchTabs } from "../../components/grid";
import { UniGame } from "./uni/UniGame";
import { MyGrades } from "./uni/MyGrades";
import { FeedbackLayer } from "./uni/feedback/FeedbackLayer";
import { FeedbackList } from "./uni/feedback/FeedbackList";
import css from "../OperatorScreen.module.css";

/** Последний выбранный филиал (префикс ub_ — принятый в проекте). */
const LS_KEY = "ub_uni_branch";
/** Подвкладка «Grades» (29.09) — свой чип в том же ряду; id не пересекается с id филиалов. */
const GRADES = "__grades";
/** Чип «Замечания» (16.10) — после «Grades». */
const NOTES = "__fb";

function readSaved(): string | null {
  try { return localStorage.getItem(LS_KEY); } catch { return null; }
}
function writeSaved(id: string) {
  try { localStorage.setItem(LS_KEY, id); } catch { /* приватный режим / запрет хранилища — не критично */ }
}

type Loaded = { branches: Branch[]; quests: Quest[] };

export function UniversityTab() {
  const { user } = useAuth();
  const [data, setData] = useState<Loaded | null>(null);
  const [ver, setVer] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(() => readSaved());
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setError(null);
    Promise.all([
      api<{ results?: Branch[] }>("/api/branches"),
      api<{ results?: Quest[] }>("/api/quests"),
    ])
      .then(([b, q]) => {
        if (!alive) return;
        setData({ branches: (b.results ?? []).filter((x) => x.active), quests: q.results ?? [] });
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : "нет связи");
      });
    return () => { alive = false; };
  }, [nonce]);

  const retry = useCallback(() => setNonce((n) => n + 1), []);

  /* По умолчанию — запомненный (если он ещё активен), иначе первый филиал
     с учебным пакетом, иначе первый. */
  const branch = useMemo(() => {
    const list = data?.branches ?? [];
    return list.find((b) => b.id === picked)
      ?? list.find((b) => (b.uni_pack ?? "").trim() !== "")
      ?? list[0]
      ?? null;
  }, [data, picked]);

  const questIds = useMemo(
    () => (branch && data ? data.quests.filter((q) => q.branch_id === branch.id).map((q) => q.id) : []),
    [branch, data],
  );

  const pick = (id: string) => {
    setPicked(id);
    writeSaved(id);
  };

  if (error) {
    return (
      <div className={css.uni}>
        <div className={css.uniMsg}>
          <div>
            Не удалось загрузить филиалы: {error}
            <div><button type="button" className={`${css.tab} ${css.uniRetry}`} onClick={retry}>Повторить</button></div>
          </div>
        </div>
      </div>
    );
  }
  if (!data) return <div className={css.uni}><div className={css.uniMsg}>Загрузка…</div></div>;
  if (!branch) return <div className={css.uni}><div className={css.uniMsg}>Нет активных филиалов</div></div>;

  const pack = (branch.uni_pack ?? "").trim();
  const onGrades = picked === GRADES;
  const onNotes = picked === NOTES;
  const inGame = !onGrades && !onNotes;
  const boss = !!user?.roles?.some((r) => r === "oo" || r === "eo");

  return (
    <FeedbackLayer className={css.uni} enabled={!onNotes}
      scope={{ pack: inGame ? pack : "", version: inGame && pack ? ver : null, branchId: inGame ? branch.id : null, branchName: inGame ? branch.name : "Юниверситет" }}>
      <div className={css.uniBar} data-fb="tabs" data-fb-name="Вкладки">
        <BranchTabs
          branches={data.branches} active={onGrades ? GRADES : onNotes ? NOTES : branch.id} onChange={pick}
          allChip={false} accent="purple" upper={false} extra={[{ id: GRADES, label: "Grades" }, { id: NOTES, label: "Замечания" }]}
        />
      </div>
      {onNotes ? (
        <FeedbackList canAllHint={boss} />
      ) : onGrades ? (
        <MyGrades branches={data.branches} quests={data.quests} />
      ) : pack ? (
        /* key — смена филиала/пакета заново монтирует движок (своё состояние прохождения) */
        <UniGame key={`${branch.id}:${pack}`} pack={pack} branchQuestIds={questIds} branchName={branch.name} onVersion={setVer} />
      ) : (
        <div className={css.uniMsg}>Обучение для этого филиала готовится</div>
      )}
    </FeedbackLayer>
  );
}
